'use client';

import { useState } from 'react';
import type { Coord } from '@/app/types/restaurant';
import type { RefObject } from 'react';
import { useMediaQuery } from '@/app/hooks/use-media-query';

type SearchHeaderProps = {
  search: string;
  setSearch: (val: string) => void;
  locationSearch: string;
  setLocationSearch: (val: string) => void;
  locationSuggestions: string[];
  showSuggestions: boolean;
  setShowSuggestions: (val: boolean) => void;
  locationError: string;
  searchLocation: (query?: string) => void;
  locationInputRef: RefObject<HTMLInputElement | null>;
  userLocation?: Coord;
  filterDistrict: string;
  setFilterDistrict: (val: string) => void;
  showOpen: boolean;
  setShowOpen: (val: boolean) => void;
  radius: number;
  setRadius: (val: number) => void;
  districts: string[];
  filteredCount: number;
};

// Radius options for picker
const RADIUS_OPTIONS = [
  { value: 500, label: '500m' },
  { value: 1000, label: '1km' },
  { value: 2000, label: '2km' },
  { value: 3000, label: '3km' },
  { value: 5000, label: '5km' },
  { value: 10000, label: '10km' },
];

// Shared chip button style
function chipClass(active: boolean) {
  return `h-9 px-4 rounded-xl text-sm font-medium transition-colors active:scale-95 ${
    active
      ? 'bg-coral-500 text-white shadow-sm'
      : 'border border-coral-200 bg-white text-gray-600'
  }`;
}

// Mobile: button → fixed bottom modal picker (proven pattern from FilterChips)
function MobilePickerModal({ label, options, value, onSelect }: {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onSelect: (val: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedLabel = options.find(o => o.value === value)?.label || label;

  return (
    <>
      <button onClick={() => setOpen(true)} className={chipClass(!!value)}>
        {selectedLabel} <span className="text-xs ml-1">▼</span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={() => setOpen(false)} />
          <div className="fixed bottom-0 left-0 right-0 z-50 bg-cream rounded-t-2xl shadow-2xl p-4 pb-8 max-h-[60vh] overflow-y-auto">
            <div className="mx-auto w-12 h-1.5 rounded-full bg-coral-200 mb-4" />
            <p className="text-sm font-semibold text-gray-700 mb-3">{label}</p>
            <div className="flex flex-wrap gap-2">
              {options.map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => { onSelect(opt.value); setOpen(false); }}
                  className={`px-4 h-10 rounded-full text-sm font-medium transition-all active:scale-95 whitespace-nowrap ${
                    value === opt.value
                      ? 'bg-coral-500 text-white shadow-md ring-2 ring-coral-200'
                      : 'border border-coral-200 bg-white text-gray-700 shadow-sm'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

// Mobile: search button → fullscreen search overlay
function MobileSearchOverlay({ search, setSearch, locationSearch, setLocationSearch, searchLocation, locationSuggestions, showSuggestions, setShowSuggestions, locationError }: {
  search: string; setSearch: (v: string) => void;
  locationSearch: string; setLocationSearch: (v: string) => void;
  searchLocation: (q?: string) => void;
  locationSuggestions: string[]; showSuggestions: boolean; setShowSuggestions: (v: boolean) => void;
  locationError: string;
}) {
  const [open, setOpen] = useState(false);
  const hasSearch = search || locationSearch;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`w-full h-10 px-4 rounded-xl text-sm text-left truncate shadow-sm ${
          hasSearch
            ? 'bg-coral-50 border border-coral-300 text-coral-800 font-medium'
            : 'border border-coral-200 bg-white text-gray-400'
        }`}
      >
        🔍 {search || locationSearch || 'Tìm quán, món, địa điểm...'}
      </button>
      {open && (
        <div className="fixed inset-0 z-50 bg-cream flex flex-col">
          {/* Header */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-coral-100">
            <button onClick={() => setOpen(false)} className="text-gray-500 text-lg">←</button>
            <span className="font-semibold text-gray-800">Tìm kiếm</span>
          </div>
          {/* Search inputs */}
          <div className="px-4 py-3 space-y-3">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">🔍</span>
              <input
                type="text"
                autoFocus
                placeholder="Tìm quán, món ăn..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-coral-200 bg-white shadow-sm text-sm"
              />
            </div>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">📍</span>
              <input
                type="text"
                placeholder="Nhập địa điểm..."
                value={locationSearch}
                onChange={(e) => setLocationSearch(e.target.value)}
                onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') { searchLocation(); setOpen(false); } }}
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-coral-200 bg-white shadow-sm text-sm"
              />
              {showSuggestions && locationSuggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full bg-white border border-coral-200 rounded-xl mt-1 shadow-xl overflow-hidden">
                  {locationSuggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setLocationSearch(suggestion);
                        setShowSuggestions(false);
                        searchLocation(suggestion);
                        setOpen(false);
                      }}
                      className="w-full text-left px-3 py-2.5 hover:bg-coral-50 text-sm border-b border-gray-50 last:border-0"
                    >
                      📍 {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {locationError && <p className="text-red-500 text-xs">{locationError}</p>}
            <button
              onClick={() => { searchLocation(); setOpen(false); }}
              className="w-full h-11 rounded-xl bg-coral-500 text-white text-sm font-medium active:scale-95"
            >
              Tìm kiếm
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Desktop: original inline inputs and native selects (these work fine on desktop)
function DesktopSearchRow(props: SearchHeaderProps) {
  const { search, setSearch, locationSearch, setLocationSearch, locationSuggestions, showSuggestions, setShowSuggestions, searchLocation, locationInputRef, locationError } = props;
  return (
    <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
      <div className="flex-1 relative">
        <label htmlFor="desktop-food-search" className="sr-only">Tìm quán, món ăn</label>
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">🔍</span>
        <input
          id="desktop-food-search"
          type="text"
          placeholder="Tìm quán, món ăn..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full h-9 pl-9 pr-3 rounded-xl border border-coral-200 bg-white shadow-sm focus-visible:ring-2 focus-visible:ring-coral-400 focus-visible:ring-offset-2 text-sm"
        />
      </div>
      <div className="relative flex-1" ref={locationInputRef}>
        <label htmlFor="desktop-location-search" className="sr-only">Nhập địa điểm</label>
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">📍</span>
        <input
          id="desktop-location-search"
          type="text"
          placeholder="Nhập địa điểm..."
          value={locationSearch}
          onChange={(e) => setLocationSearch(e.target.value)}
          onFocus={() => locationSuggestions.length > 0 && setShowSuggestions(true)}
          onKeyDown={(e) => e.key === 'Enter' && searchLocation()}
          className="w-full h-9 pl-9 pr-3 rounded-xl border border-coral-200 bg-white shadow-sm text-sm focus-visible:ring-2 focus-visible:ring-coral-400 focus-visible:ring-offset-2"
        />
        {showSuggestions && locationSuggestions.length > 0 && (
          <div className="absolute z-50 left-0 right-0 top-full bg-white border border-coral-200 rounded-xl mt-1 shadow-xl overflow-hidden">
            {locationSuggestions.map((suggestion, idx) => (
              <button
                key={idx}
                onClick={() => { setLocationSearch(suggestion); setShowSuggestions(false); searchLocation(suggestion); }}
                className="w-full text-left px-3 py-2 hover:bg-coral-50 text-sm"
              >
                📍 {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function DesktopFilterRow(props: SearchHeaderProps) {
  const { filterDistrict, setFilterDistrict, showOpen, setShowOpen, radius, setRadius, userLocation, districts } = props;
  return (
    <div className="flex flex-wrap gap-2 items-center">
      <div className="relative">
        <select value={filterDistrict} onChange={(e) => setFilterDistrict(e.target.value)} className="h-9 pl-3 pr-8 rounded-xl border border-coral-200 bg-white text-sm appearance-none focus-visible:ring-2 focus-visible:ring-coral-400 focus-visible:ring-offset-2">
          <option value="">Tất cả quận</option>
          {districts.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600 text-xs">▼</span>
      </div>
      <button onClick={() => setShowOpen(!showOpen)} className={chipClass(showOpen)}>⏰ Đang mở</button>
      <div className="relative">
        <select value={radius} onChange={(e) => setRadius(Number(e.target.value))} className="h-9 pl-3 pr-8 rounded-xl border border-coral-200 bg-white text-sm appearance-none focus-visible:ring-2 focus-visible:ring-coral-400 focus-visible:ring-offset-2">
          {RADIUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-600 text-xs">▼</span>
      </div>
    </div>
  );
}

export function SearchHeader(props: SearchHeaderProps) {
  const isTabletUp = useMediaQuery('(min-width: 768px)');

  return (
    <header className="bg-cream border-b border-coral-100 sticky top-0 z-40 shadow-sm">
      <div className="px-4 py-3 space-y-2">
        <p className="text-xs text-gray-600">
          🍜 Khám phá <span className="font-semibold text-coral-800">{props.filteredCount}</span> quán ăn ngon Sài Gòn
        </p>

        {isTabletUp ? (
          <>
            <DesktopSearchRow {...props} />
            <DesktopFilterRow {...props} />
          </>
        ) : (
          /* Mobile: inline search inputs + filter chips */
          <div className="space-y-2">
            {/* Row 1: Food search + Location search */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <label htmlFor="mobile-food-search" className="sr-only">Tìm quán, món ăn</label>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">🔍</span>
                <input
                  id="mobile-food-search"
                  type="text"
                  placeholder="Tìm quán, món ăn..."
                  value={props.search}
                  onChange={(e) => props.setSearch(e.target.value)}
                  className="w-full h-9 pl-9 pr-3 rounded-xl border border-coral-200 bg-white shadow-sm text-sm"
                />
              </div>
              <div className="relative flex-1">
                <label htmlFor="mobile-location-search" className="sr-only">Nhập địa điểm</label>
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">📍</span>
                <input
                  id="mobile-location-search"
                  type="text"
                  placeholder="Nhập địa điểm..."
                  value={props.locationSearch}
                  onChange={(e) => props.setLocationSearch(e.target.value)}
                  onFocus={() => props.locationSuggestions.length > 0 && props.setShowSuggestions(true)}
                  onKeyDown={(e) => e.key === 'Enter' && props.searchLocation()}
                  className="w-full h-9 pl-9 pr-3 rounded-xl border border-coral-200 bg-white shadow-sm text-sm"
                />
                {props.showSuggestions && props.locationSuggestions.length > 0 && (
                  <div className="absolute z-50 left-0 right-0 top-full bg-white border border-coral-200 rounded-xl mt-1 shadow-xl overflow-hidden">
                    {props.locationSuggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => { props.setLocationSearch(suggestion); props.setShowSuggestions(false); props.searchLocation(suggestion); }}
                        className="w-full text-left px-3 py-2.5 hover:bg-coral-50 text-sm border-b border-gray-50 last:border-0"
                      >
                        📍 {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {props.locationError && <p className="text-red-500 text-xs">{props.locationError}</p>}
            {/* Row 2: Filter chips */}
            <div className="flex flex-wrap gap-2 items-center">
              <MobilePickerModal
                label="Chọn quận"
                value={props.filterDistrict}
                options={[{ value: '', label: 'Tất cả quận' }, ...props.districts.map(d => ({ value: d, label: d }))]}
                onSelect={props.setFilterDistrict}
              />
              <button onClick={() => props.setShowOpen(!props.showOpen)} className={chipClass(props.showOpen)}>⏰ Đang mở</button>
              <MobilePickerModal
                label="Bán kính"
                value={String(props.radius)}
                options={RADIUS_OPTIONS.map(o => ({ value: String(o.value), label: o.label }))}
                onSelect={(v) => props.setRadius(Number(v))}
              />
            </div>
          </div>
        )}

        {props.locationError && isTabletUp && <p className="text-red-500 text-xs mt-1">{props.locationError}</p>}
      </div>
    </header>
  );
}
